(() => {
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const group = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return (format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_with_comma_separator') return group('.') + ',' + decimals;
      if (key === 'amount_no_decimals') return group(',');
      if (key === 'amount_no_decimals_with_comma_separator') return group('.');
      if (key === 'amount_with_apostrophe_separator') return group("'") + '.' + decimals;
      return group(',') + '.' + decimals;
    });
  };

  class GsProductDuoPrice extends HTMLElement {
    connectedCallback() {
      this.onChange = (event) => {
        const detail = event.detail;
        if (detail.section !== this.dataset.section) return;
        this.hidden = detail.mode !== 'set';
        this.querySelector('[data-duo-price-now]').textContent = detail.mainNet;
        const was = this.querySelector('[data-duo-price-was]');
        was.textContent = detail.mainWas || '';
        was.hidden = !detail.mainWas;
      };
      document.addEventListener('gs:duo:change', this.onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('gs:duo:change', this.onChange);
    }
  }

  class GsProductDuo extends HTMLElement {
    connectedCallback() {
      this.pct = Number(this.dataset.percent || 20);
      this.mine = this.dataset.mine;
      this.productId = this.dataset.productId;
      this.configurator = this.closest('.gs-configurator') || document;
      this.mode = 'solo';
      this.editing = false;
      this.custom = false;
      this.second = { group: this.mine, productId: this.productId, variantId: null };

      this.onSizeChange = (event) => {
        if (event.target.closest('.variations')) this.render();
      };
      this.configurator.addEventListener('change', this.onSizeChange);

      this.addEventListener('click', (event) => this.onClick(event));
      this.ref('[data-duo-submit]').addEventListener('click', () => this.addToCart());

      this.render();
    }

    disconnectedCallback() {
      this.configurator.removeEventListener('change', this.onSizeChange);
    }

    ref(selector) {
      return this.querySelector(selector);
    }

    money(cents) {
      return formatMoney(cents, this.dataset.moneyFormat);
    }

    from(cents) {
      return this.dataset.labelFrom.replace('__PRICE__', this.money(cents));
    }

    off(cents, qty = 1) {
      return Math.round((cents * qty * this.pct) / 100);
    }

    variantsOf(productId) {
      return Array.from(this.querySelectorAll(`[data-duo-size][data-product-id="${productId}"]`)).map((el) => ({
        el,
        id: el.dataset.variantId,
        label: el.dataset.label,
        price: Number(el.dataset.price),
        available: el.dataset.available === 'true'
      }));
    }

    swatchOf(productId) {
      return this.querySelector(`[data-duo-swatch][data-product-id="${productId}"]`);
    }

    cheapest(list) {
      return list.reduce((low, v) => (low === null || v.price < low ? v.price : low), null);
    }

    selectedLabel() {
      const radio = this.configurator.querySelector('.variations .product-form__input--block input[type="radio"]:checked');
      if (radio) return radio.value;
      const select = this.configurator.querySelector('.variations select');
      return select && select.value ? select.value : null;
    }

    mainVariant() {
      const label = this.selectedLabel();
      return label ? this.variantsOf(this.productId).find((v) => v.label === label) || null : null;
    }

    secondVariant() {
      const variants = this.variantsOf(this.second.productId);
      if (!this.custom) {
        const main = this.mainVariant();
        return main ? variants.find((v) => v.label === main.label) || null : null;
      }
      return variants.find((v) => v.id === this.second.variantId) || null;
    }

    onClick(event) {
      const mode = event.target.closest('[data-duo-mode]');
      if (mode) return this.setMode(mode.dataset.duoMode);

      if (event.target.closest('[data-duo-edit]')) {
        this.editing = true;
        return this.render();
      }

      const type = event.target.closest('[data-duo-type]');
      if (type && type.dataset.duoType !== this.second.group) {
        const key = type.dataset.duoType;
        const swatches = Array.from(this.querySelectorAll(`[data-duo-swatch][data-group="${key}"]`));
        const first = swatches.find((s) => !s.classList.contains('is-soldout')) || swatches[0];
        this.second = { group: key, productId: first.dataset.productId, variantId: null };
        this.custom = true;
        return this.render();
      }

      const swatch = event.target.closest('[data-duo-swatch]');
      if (swatch && !swatch.classList.contains('is-soldout')) {
        if (!this.custom && swatch.dataset.productId === this.second.productId) return;
        const current = this.secondVariant();
        const keep = current
          ? this.variantsOf(swatch.dataset.productId).find((v) => v.label === current.label && v.available)
          : null;
        this.second = { group: swatch.dataset.group, productId: swatch.dataset.productId, variantId: keep ? keep.id : null };
        this.custom = true;
        return this.render();
      }

      const size = event.target.closest('[data-duo-size]');
      if (size && size.dataset.available === 'true') {
        this.second.variantId = size.dataset.variantId;
        this.custom = true;
        this.editing = false;
        this.render();
      }
    }

    setMode(mode) {
      if (mode === this.mode) return;
      this.mode = mode;
      if (mode === 'solo') this.editing = false;
      this.render();
    }

    totals(main, sec) {
      const full = main.price + sec.price;
      const off = main.id === sec.id ? this.off(main.price, 2) : this.off(main.price) + this.off(sec.price);
      return { full, net: full - off, off };
    }

    renderPicker(sec) {
      this.querySelectorAll('[data-duo-type]').forEach((el) => {
        const on = el.dataset.duoType === this.second.group;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-pressed', String(on));
      });
      this.querySelectorAll('[data-duo-swatch]').forEach((el) => {
        const on = el.dataset.productId === this.second.productId;
        el.hidden = el.dataset.group !== this.second.group;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-pressed', String(on));
      });
      this.querySelectorAll('[data-duo-size]').forEach((el) => {
        const on = !!sec && el.dataset.variantId === sec.id;
        el.hidden = el.dataset.productId !== this.second.productId;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-pressed', String(on));
      });
      const swatch = this.swatchOf(this.second.productId);
      this.ref('[data-duo-colour]').textContent = swatch ? swatch.dataset.colour : '';
      this.ref('[data-duo-size-value]').textContent = sec ? sec.label : '';
    }

    render() {
      const isSet = this.mode === 'set';
      const main = this.mainVariant();
      const sec = this.secondVariant();
      const mainVariants = this.variantsOf(this.productId);
      const swatch = this.swatchOf(this.second.productId);

      this.dataset.mode = this.mode;
      this.querySelectorAll('[data-duo-mode]').forEach((el) => {
        const on = el.dataset.duoMode === this.mode;
        el.setAttribute('aria-pressed', String(on));
        el.closest('.gs-duo__option').classList.toggle('is-selected', on);
      });

      this.ref('[data-duo-solo-price]').textContent = main ? this.money(main.price) : this.from(this.cheapest(mainVariants));

      const setPrice = this.ref('[data-duo-set-price]');
      const setWas = this.ref('[data-duo-set-was]');
      const saving = this.ref('[data-duo-saving]');
      let total = null;
      if (main && sec) {
        total = this.totals(main, sec);
        setPrice.textContent = this.money(total.net);
        setWas.textContent = this.money(total.full);
        setWas.hidden = false;
        saving.textContent = this.dataset.labelSave.replace('__AMOUNT__', this.money(total.off));
      } else {
        const lowMain = this.cheapest(mainVariants);
        const lowSecond = sec ? sec.price : this.cheapest(this.variantsOf(this.second.productId));
        setPrice.textContent = this.from(lowMain + lowSecond - this.off(lowMain) - this.off(lowSecond));
        setWas.hidden = true;
        saving.textContent = this.dataset.labelSavePct;
      }

      this.ref('[data-duo-items]').hidden = !isSet;
      this.ref('[data-duo-main-size]').textContent = main ? main.label : this.dataset.labelChoose;
      if (swatch) {
        this.ref('[data-duo-second-img]').src = swatch.dataset.image;
        this.ref('[data-duo-second-name]').textContent = swatch.dataset.title;
      }
      this.ref('[data-duo-second-size]').textContent = sec ? sec.label : this.dataset.labelChoose;

      const edit = this.ref('[data-duo-edit]');
      edit.hidden = this.editing;
      edit.setAttribute('aria-expanded', String(this.editing));
      this.ref('[data-duo-picker]').hidden = !(isSet && this.editing);
      if (isSet && this.editing) this.renderPicker(sec);

      const soldOut = (main && !main.available) || (sec && !sec.available);
      const ready = !!(isSet && main && sec && !soldOut);
      const submit = this.ref('[data-duo-submit]');
      this.ref('[data-duo-actions]').hidden = !isSet;
      submit.disabled = !ready;
      this.ref('[data-duo-submit-text]').textContent = soldOut
        ? this.dataset.labelSoldOut
        : ready
        ? this.dataset.labelAdd
        : this.dataset.labelChoose;

      document.dispatchEvent(
        new CustomEvent('gs:duo:change', {
          detail: {
            section: this.dataset.section,
            mode: this.mode,
            ready,
            button: submit,
            title: this.second.productId === this.productId
              ? '2× ' + this.ref('[data-duo-main-name]').textContent
              : this.ref('[data-duo-main-name]').textContent + ' + ' + (swatch ? swatch.dataset.title : ''),
            price: isSet ? setPrice.textContent : this.ref('[data-duo-solo-price]').textContent,
            was: isSet && total ? this.money(total.full) : null,
            mainNet: main ? this.money(main.price - this.off(main.price)) : this.from(this.cheapest(mainVariants) - this.off(this.cheapest(mainVariants))),
            mainWas: main ? this.money(main.price) : null
          }
        })
      );
    }

    addToCart() {
      const main = this.mainVariant();
      const sec = this.secondVariant();
      const submit = this.ref('[data-duo-submit]');
      const error = this.ref('[data-duo-error]');
      if (submit.disabled || !main || !sec) return;

      const items =
        main.id === sec.id
          ? [{ id: Number(main.id), quantity: 2 }]
          : [
              { id: Number(main.id), quantity: 1 },
              { id: Number(sec.id), quantity: 1 }
            ];

      submit.classList.add('loading');
      submit.disabled = true;
      error.hidden = true;

      const url = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js';
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) {
            error.textContent = body.description || body.message || '';
            error.hidden = !error.textContent;
            return;
          }
          document.documentElement.classList.add('js-ajax-cart-drawer-opened');
          if (typeof liquidAjaxCart !== 'undefined' && liquidAjaxCart.update) liquidAjaxCart.update();
        })
        .catch(() => {})
        .finally(() => {
          submit.classList.remove('loading');
          this.render();
        });
    }
  }

  if (!customElements.get('gs-product-duo-price')) customElements.define('gs-product-duo-price', GsProductDuoPrice);
  if (!customElements.get('gs-product-duo')) customElements.define('gs-product-duo', GsProductDuo);
})();
